import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { formatUsd } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { AlertTriangle, ArrowLeft, ExternalLink, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const FEATURE_LABELS = {
  classification: "Message classification",
  image_description: "Photo descriptions",
  voice_note: "WhatsApp voice notes",
  daily_summary: "Daily summaries",
  weekly_summary: "Weekly summaries",
  summary_rewrite: "Client text rewrite (voice guidance)",
  manager_voice_note: "Manager voice guidance",
  other: "Other",
};

const PRICE_FIELDS = [
  { key: "input_per_1m", label: "Input / 1M tokens" },
  { key: "cached_input_per_1m", label: "Cached input / 1M" },
  { key: "output_per_1m", label: "Output / 1M tokens" },
  { key: "per_minute", label: "Audio / minute" },
];

const isoDay = (d) => {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return isoDay(d); };
const fmtInt = (n) => (Number(n) || 0).toLocaleString("en-IN");
const fmtDay = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

function Stat({ label, value, hint }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="text-xs text-muted-foreground uppercase tracking-wider">{label}</div>
        <div className="text-2xl font-heading font-bold mt-1 tabular-nums">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-md border border-border bg-card px-3 py-2 text-xs shadow-sm">
      <div className="font-medium">{fmtDay(row.day)}</div>
      <div className="tabular-nums">{formatUsd(row.cost_usd)} · {row.calls} calls</div>
    </div>
  );
}

function BreakdownTable({ rows, nameHeader, renderName, testid }) {
  return (
    <Table data-testid={testid}>
      <TableHeader>
        <TableRow>
          <TableHead>{nameHeader}</TableHead>
          <TableHead className="text-right">Calls</TableHead>
          <TableHead className="text-right hidden sm:table-cell">Tokens in / out</TableHead>
          <TableHead className="text-right">Cost</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow><TableCell colSpan={4} className="text-muted-foreground">No AI calls in this range.</TableCell></TableRow>
        ) : rows.map((r, i) => (
          <TableRow key={i}>
            <TableCell>{renderName(r)}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtInt(r.calls)}</TableCell>
            <TableCell className="text-right tabular-nums hidden sm:table-cell text-muted-foreground">
              {r.audio_seconds > 0 && !r.input_tokens
                ? `${(r.audio_seconds / 60).toFixed(1)} min audio`
                : `${fmtInt(r.input_tokens)} / ${fmtInt(r.output_tokens)}`}
            </TableCell>
            <TableCell className="text-right tabular-nums font-medium">{formatUsd(r.cost_usd)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function PricingCard({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [newModel, setNewModel] = useState("");

  const load = useCallback(() => {
    api.get("/ai-pricing").then((r) => {
      setRows(r.data);
      setDraft(Object.fromEntries(r.data.map((p) => [p.model, Object.fromEntries(PRICE_FIELDS.map((f) => [f.key, String(p[f.key] ?? 0)]))])));
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (model) => {
    const body = Object.fromEntries(PRICE_FIELDS.map((f) => [f.key, Number(draft[model]?.[f.key]) || 0]));
    try {
      await api.put(`/ai-pricing/${encodeURIComponent(model)}`, body);
      toast.success(`Price for ${model} saved — applies to new AI calls`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (model) => {
    if (!window.confirm(`Remove the price for ${model}?`)) return;
    try {
      await api.delete(`/ai-pricing/${encodeURIComponent(model)}`);
      toast.success("Price removed");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const add = async () => {
    const model = newModel.trim();
    if (!model) return;
    try {
      await api.put(`/ai-pricing/${encodeURIComponent(model)}`, {});
      setNewModel("");
      toast.success(`${model} added — fill in its prices`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Card><CardContent className="p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-heading text-lg font-semibold">Model prices (USD)</h2>
          <p className="text-xs text-muted-foreground">
            Used to calculate each AI call's cost when it happens. Changing a price affects new calls only.
          </p>
        </div>
        <a href="https://openai.com/api/pricing" target="_blank" rel="noreferrer"
          className="text-xs text-primary inline-flex items-center gap-1 hover:underline">
          Official OpenAI prices <ExternalLink className="h-3 w-3" />
        </a>
      </div>
      <div className="space-y-3">
        {rows.map((p) => (
          <div key={p.model} className="border border-border rounded-md p-3 space-y-2" data-testid={`ai-price-${p.model}`}>
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm font-medium">{p.model}</span>
              {p.in_use && <Badge variant="secondary">in use</Badge>}
              {p.in_use && PRICE_FIELDS.every((f) => !p[f.key]) && (
                <Badge variant="destructive">no price set</Badge>
              )}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-[repeat(4,1fr)_auto] gap-2 items-end">
              {PRICE_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label className="text-xs text-muted-foreground font-normal">{f.label}</Label>
                  <Input type="number" min="0" step="any" disabled={!canEdit} className="tabular-nums"
                    value={draft[p.model]?.[f.key] ?? ""}
                    onChange={(e) => setDraft({ ...draft, [p.model]: { ...draft[p.model], [f.key]: e.target.value } })} />
                </div>
              ))}
              {canEdit && (
                <div className="flex gap-1 col-span-2 lg:col-span-1">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => save(p.model)}>Save</Button>
                  {!p.in_use && (
                    <Button size="sm" variant="ghost" className="rounded-full" onClick={() => remove(p.model)} aria-label={`Remove ${p.model}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {canEdit && (
        <div className="flex gap-2 max-w-md">
          <Input placeholder="Add a model, e.g. gpt-4o-mini" value={newModel}
            onChange={(e) => setNewModel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={add}>
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
      )}
    </CardContent></Card>
  );
}

export default function AiUsage() {
  const { user } = useAuth();
  const canEdit = user?.role === "ADMIN";
  const [searchParams] = useSearchParams();
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(isoDay(new Date()));
  const [projectId, setProjectId] = useState(searchParams.get("project_id") || "all");
  const [projects, setProjects] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)); }, []);

  useEffect(() => {
    const params = { from, to };
    if (projectId !== "all") params.project_id = projectId;
    api.get("/ai-usage", { params })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [from, to, projectId]);

  const setRange = (days) => { setFrom(daysAgo(days - 1)); setTo(isoDay(new Date())); };

  return (
    <div className="space-y-6">
      <div>
        <Link to="/settings" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
          data-testid="ai-usage-back">
          <ArrowLeft className="h-4 w-4" /> Settings
        </Link>
        <h1 className="font-heading text-3xl font-bold tracking-tight">AI Usage</h1>
        <p className="text-muted-foreground mt-1">
          Estimated OpenAI cost of every AI call the app makes. The final bill is on{" "}
          <a href="https://platform.openai.com/usage" target="_blank" rel="noreferrer" className="text-primary hover:underline">
            platform.openai.com/usage
          </a>.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3" data-testid="ai-usage-filters">
        <div>
          <Label className="text-xs">From</Label>
          <Input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} className="w-40" />
        </div>
        <div>
          <Label className="text-xs">To</Label>
          <Input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} className="w-40" />
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((n) => (
            <Button key={n} size="sm" variant="outline" className="rounded-full" onClick={() => setRange(n)}>{n}d</Button>
          ))}
        </div>
        <div className="w-full sm:w-64">
          <Label className="text-xs">Project</Label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger data-testid="ai-usage-project"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!data ? (
        <div className="text-muted-foreground">Loading usage…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Today" value={formatUsd(data.today_usd)} />
            <Stat label="This month" value={formatUsd(data.month_usd)} />
            <Stat label="All time" value={formatUsd(data.all_time_usd)} />
            <Stat label="Selected range" value={formatUsd(data.totals.cost_usd)} hint={`${fmtInt(data.totals.calls)} AI calls`} />
          </div>

          {data.unpriced_calls > 0 && (
            <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {data.unpriced_calls} call(s) in this range used a model with no price set, so they count as $0. Set its price below.
            </div>
          )}

          <Card><CardContent className="p-5">
            <h2 className="font-heading text-lg font-semibold mb-4">Daily cost</h2>
            <div className="h-64" data-testid="ai-usage-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.by_day} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="day" tickFormatter={fmtDay} tickLine={false} axisLine={false}
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} minTickGap={24} />
                  <YAxis tickFormatter={(v) => `$${v}`} tickLine={false} axisLine={false} width={48}
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(var(--muted))" }} />
                  <Bar dataKey="cost_usd" fill="hsl(var(--chart-3))" radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent></Card>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card><CardContent className="p-5">
              <h2 className="font-heading text-lg font-semibold mb-2">By project</h2>
              <BreakdownTable rows={data.by_project} nameHeader="Project" testid="ai-usage-by-project"
                renderName={(r) => r.project_name || <span className="text-muted-foreground">Not linked to a project</span>} />
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <h2 className="font-heading text-lg font-semibold mb-2">By feature</h2>
              <BreakdownTable rows={data.by_feature} nameHeader="Feature" testid="ai-usage-by-feature"
                renderName={(r) => FEATURE_LABELS[r.feature] || r.feature} />
            </CardContent></Card>
          </div>

          <Card><CardContent className="p-5">
            <h2 className="font-heading text-lg font-semibold mb-2">By model</h2>
            <BreakdownTable rows={data.by_model} nameHeader="Model" testid="ai-usage-by-model"
              renderName={(r) => <span className="font-mono text-sm">{r.model}</span>} />
          </CardContent></Card>

          <Card><CardContent className="p-5">
            <h2 className="font-heading text-lg font-semibold mb-2">Latest calls</h2>
            <Table data-testid="ai-usage-recent">
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Feature</TableHead>
                  <TableHead className="hidden md:table-cell">Project</TableHead>
                  <TableHead className="hidden sm:table-cell">Model</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recent.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-muted-foreground">No AI calls in this range.</TableCell></TableRow>
                ) : data.recent.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {new Date(r.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </TableCell>
                    <TableCell>{FEATURE_LABELS[r.feature] || r.feature}</TableCell>
                    <TableCell className="hidden md:table-cell">{r.project_name || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="hidden sm:table-cell font-mono text-xs">{r.model}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatUsd(r.cost_usd)}
                      {!r.priced && <span className="text-amber-700 text-xs ml-1">(no price)</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </>
      )}

      <PricingCard canEdit={canEdit} />
    </div>
  );
}
